import { Topbar } from '../../components/Topbar/Topbar';
import { Badge, Container, Modal, Table } from 'react-bootstrap';
import { Footer } from '../../components/Footer/Footer';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useEffect, useState } from 'react';
import { DEFAULT_JSON_VIEWER_VALUE, formatSecondsTimestamp, stringify } from '../../utils/utils';
import { UserUID } from '../../components/UserUID/UserUID';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import {
  faArrowDown,
  faChevronLeft,
  faChevronRight,
  faCode,
  faDatabase,
  faFolderOpen,
  faGraduationCap,
  faQuestion,
  faTags,
  faUser,
  faUserEdit,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { IconButton } from '../../components/IconButton/IconButton';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Copyable } from '../../components/Copyable/Copyable';
import { Title } from '../../components/Title/Title';

const LogsComponent = () => {
  const { getLogs } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [logDetailsBefore, setLogDetailsBefore] = useState(DEFAULT_JSON_VIEWER_VALUE);
  const [logDetailsAfter, setLogDetailsAfter] = useState(DEFAULT_JSON_VIEWER_VALUE);
  const [logDetailsModalOpen, setLogDetailsModalOpen] = useState(false);
  useEffect(() => {
    if (!logDetailsModalOpen) {
      setLogDetailsBefore(DEFAULT_JSON_VIEWER_VALUE);
      setLogDetailsAfter(DEFAULT_JSON_VIEWER_VALUE);
    }
  }, [logDetailsModalOpen]);

  useEffect(() => {
    setLoading(true);
    getLogs(currentPage * 50)
      .then(setLogs)
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage]);

  return (
    <>
      <Title>Logs</Title>

      <Topbar />

      <Container className={'my-5'}>
        <div className={'display-4'}>Logs</div>

        <Table striped bordered variant={'dark'} className={'mt-4'}>
          {!loading && (
            <thead>
              <tr>
                <th>Target</th>
                <th>Event</th>
                <th>
                  <div className={'d-flex justify-content-between align-items-center'}>
                    Timestamp <FontAwesomeIcon className={'text-muted'} icon={faArrowDown} />
                  </div>
                </th>
                <th>Author</th>
                <th>Element ID</th>
                <th style={{ width: '1px', whiteSpace: 'nowrap' }} className={'text-center'}>
                  Data
                </th>
              </tr>
            </thead>
          )}
          <tbody>
            {loading && (
              <tr>
                <td colSpan={999}>
                  <FullscreenLoader fullscreen={false} />
                </td>
              </tr>
            )}
            {!loading && !logs.length && (
              <tr>
                <td colSpan={999} className={'text-center'}>
                  Nessun log da visualizzare
                </td>
              </tr>
            )}
            {!loading &&
              !!logs.length &&
              logs.map((log) => (
                <tr key={log.id}>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Badge variant={'light'}>
                      <FontAwesomeIcon
                        className={'mr-1'}
                        fixedWidth={true}
                        icon={(() => {
                          switch (log.target) {
                            case 'TAG':
                              return faTags;
                            case 'AUTHOR':
                              return faGraduationCap;
                            case 'STORAGE':
                              return faFolderOpen;
                            case 'VIDEO':
                              return faVideo;
                            case 'USER':
                              return faUserEdit;
                            case 'CACHE':
                              return faDatabase;
                            default:
                              return faQuestion;
                          }
                        })()}
                      />
                      {log.target}
                    </Badge>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Badge
                      variant={(() => {
                        switch (log.event) {
                          case 'CREATE':
                            return 'success';
                          case 'UPDATE':
                            return 'primary';
                          case 'DELETE':
                          case 'CLEAR':
                            return 'danger';
                          default:
                            return 'secondary';
                        }
                      })()}
                    >
                      {log.event}
                    </Badge>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>{formatSecondsTimestamp(log.timestamp._seconds, 'dd/MM/y HH:mm')}</td>
                  <td style={{ verticalAlign: 'middle' }}>{!!log.by ? <UserUID uid={log.by} /> : <Badge variant={'warning'}>SYSTEM</Badge>}</td>
                  <td style={{ verticalAlign: 'middle' }}>{log.target == 'USER' ? <UserUID uid={log.element_id} /> : <Copyable text={log.element_id} />}</td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {!!log.data && (
                      <IconButton
                        icon={faCode}
                        onClick={() => {
                          if (log.data.before || log.data.after) {
                            setLogDetailsBefore(stringify(log.data.before || {}));
                            setLogDetailsAfter(stringify(log.data.after || {}));
                          } else {
                            if (log.event == 'DELETE') setLogDetailsBefore(stringify(log.data));
                            else setLogDetailsAfter(stringify(log.data));
                          }
                          setLogDetailsModalOpen(true);
                        }}
                      />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
          {!loading && (
            <tfoot>
              <tr>
                <td colSpan={999}>
                  <div className={'d-flex justify-content-between align-items-center'}>
                    <span>Pagina {currentPage + 1}</span>
                    <div className={'d-flex'}>
                      {!!currentPage && (
                        <IconButton
                          icon={faChevronLeft}
                          onClick={() => {
                            setCurrentPage((current) => current - 1);
                          }}
                        />
                      )}
                      {!!logs.length && (
                        <IconButton
                          icon={faChevronRight}
                          onClick={() => {
                            setCurrentPage((current) => current + 1);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </Container>

      <Footer />

      <Modal show={logDetailsModalOpen} onHide={() => setLogDetailsModalOpen(false)} size={'xl'} scrollable={true}>
        <Modal.Header closeButton>
          <Modal.Title>Dettaglio evento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReactDiffViewer
            oldValue={logDetailsBefore}
            newValue={logDetailsAfter}
            splitView={true}
            leftTitle={'Before'}
            rightTitle={'After'}
            compareMethod={DiffMethod.WORDS}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LogsComponent;
